import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { getFinancialYear } from "../../components/common/Util/Util";
import { getS3UrlByKeyBase64 } from "../../API/API";

const LoanApplicationPDF = forwardRef(({ data }, ref) => {
  const application = useMemo(() => data ?? {}, [data]);
    // const user = application?.userId || {};
    const directors = application?.directorDetails || [];
    // const documents = application?.documents || [];
  const contentRef = useRef();

  const loanDoc = useMemo(() => {
    const documents = application?.documents;

    const typeToKeyMap = {
      BankStatement: 'bankStatement', SLLoan: 'slLoan', DebitProfile: 'debitProfile',
      CoLetteral: 'coLetteral', OrderCopy: 'orderCopy', ContractCopy: 'contractCopy',
      Certification: 'certification', CreditRating: 'creditRating', CompanyBrief: 'companyBrief',
      MOA: 'moa', AOA: 'aoa', Shareholding: 'shareholding',
      BalanceSheet: 'auditedBalanceFYPrev1',
      'Aadhar Propreitor': 'AadharCard', 'PAN Propreitor': 'panCard',
      ProvisionalBalanceSheet: 'provisionalBalanceSheet',
      AuditedBalanceFYPrev1: 'auditedBalanceFYPrev1',
      AuditedBalanceFYPrev2: 'auditedBalanceFYPrev2',
      'Aadhar Director': 'AadharCard', 'PAN Director': 'panCard',
      'Aadhar Partner': 'AadharCard', 'PAN Partner': 'panCard',
      BorrowerPanCard: 'borrowerPanCard', IncorporationDateProof: 'incorporationDateProof',
      'Other Relevant Docs': 'other',
    };

    const MULTI_FILE_KEYS = new Set(['bankStatement', 'slLoan', 'panCard', 'AadharCard']);

    const docsFromDocumentsArray =
      Array.isArray(documents) && documents.length > 0
        ? documents.reduce((acc, doc) => {
            const key = typeToKeyMap[doc?.documentType];

            const docValue = {
              url: doc?.documentPath ?? doc?.url ?? null,
              documentPath: doc?.documentPath ?? null,
              s3Key: doc?.s3Key ?? null,
              documentName: doc?.documentName ?? null,
              fileType: doc?.fileType ?? null,
            };

            if (!key) {
              acc.otherDocuments = acc.otherDocuments || [];
              acc.otherDocuments.push(docValue);
              return acc;
            }

            if (MULTI_FILE_KEYS.has(key)) {
              acc[key] = acc[key] ?? [];
              acc[key].push(docValue);
            } else {
              acc[key] = docValue;
            }
            return acc;
          }, {})
        : {};

    if (application?.loanDoc && Object.keys(application.loanDoc).length > 0) {
      const merged = { ...docsFromDocumentsArray, ...application.loanDoc };

      MULTI_FILE_KEYS.forEach((key) => {
        const fromDocs = docsFromDocumentsArray?.[key];
        const fromLoanDoc = application?.loanDoc?.[key];
        if (Array.isArray(fromDocs) && Array.isArray(fromLoanDoc)) {
          merged[key] = [...fromDocs, ...fromLoanDoc];
        }
      });

      return merged;
    }

    if (!Array.isArray(documents) || documents.length === 0) return {};
    return docsFromDocumentsArray;
  }, [application?.loanDoc, application?.documents]);

  const handleDownloadPDF = async () => {
    const input = contentRef.current;
    if (!input) return;

    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = pdfWidth / imgWidth;
    const scaledHeight = imgHeight * ratio;

    let heightLeft = scaledHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, pdfWidth, scaledHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position -= pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, scaledHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`${application.applicationNumber || "application"}.pdf`);
  };

  useImperativeHandle(ref, () => ({
    downloadPDF: handleDownloadPDF,
  }));
  const currentDate = useMemo(() => new Date(), []);
  const currentFinancialYear = useMemo(
    () => getFinancialYear(currentDate),
    [currentDate]
  );

  const prev1FYLabel = useMemo(() => {
    const prevYear = currentDate.getFullYear() - 1;
    const prevMonth = currentDate.getMonth() + 1;
    const fy = prevMonth >= 4 ? `${prevYear}-${prevYear + 1}` : `${prevYear - 1}-${prevYear}`;
    return `Audited Balance Sheet FY ${fy}`;
  }, [currentDate]);

  const prev2FYLabel = useMemo(() => {
    const prevYear = currentDate.getFullYear() - 2;
    const prevMonth = currentDate.getMonth() + 1;
    const fy = prevMonth >= 4 ? `${prevYear}-${prevYear + 1}` : `${prevYear - 1}-${prevYear}`;
    return `Audited Balance Sheet FY ${fy}`;
  }, [currentDate]);

  const formatWithCommas = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const tableStyle = {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: "9pt",
    wordWrap: "break-word",
  };

  const thTdStyle = {
    border: "1px solid #999",
    padding: "4px 6px",
    verticalAlign: "top",
  };

  const sectionHeadingStyle = {
    ...thTdStyle,
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    fontSize: "10pt",
    textAlign: "left",
  };

  const encodeS3KeyToBase64 = (rawKey) => {
    const text = String(rawKey ?? "");
    const binary = encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );
    return btoa(binary);
  };

  const isUrl = (str) => str?.constructor === String && /^https?:\/\//.test(str);

  const openPresignedS3Url = async (s3KeyOrUrl) => {
    if (!s3KeyOrUrl) return;
    
    if (isUrl(s3KeyOrUrl)) {
      window.open(s3KeyOrUrl, "_blank");
      return;
    }
    
    try {
      const response = await getS3UrlByKeyBase64(encodeS3KeyToBase64(s3KeyOrUrl));
      const url = [
        response?.result?.url, 
        response?.url, 
        response?.presignedUrl, 
        response?.result, 
        response?.data,
      ].find((v) => v?.constructor === String && v.length);
      url && window.open(url, "_blank");
    } catch (e) {
      // ignore
    }
  };

  const openMultipleDocumentUrls = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;

    items.forEach((item) => {
      const href = getDocumentHref(item);
      if (href) {
        openPresignedS3Url(href);
      }
    });
  };

  const entityDocs = [
    { key: "borrowerPanCard", label: "Entity PAN", condition: () => true },

    {
      key: "moa",
      label: "MOA",
      condition: (entityType) =>
        entityType !== "partnership_firm" &&
        entityType !== "propreitorship_firm",
    },

    {
      key: "aoa",
      label: "AOA",
      condition: (entityType) =>
        entityType !== "partnership_firm" &&
        entityType !== "propreitorship_firm",
    },

    {
      key: "shareholding",
      label: "Shareholding Pattern",
      condition: (entityType) =>
        entityType !== "partnership_firm" &&
        entityType !== "propreitorship_firm",
    },

    {
      key: "incorporationDateProof",
      label: "Incorporation Date Proof",
      condition: (entityType) => entityType === "partnership_firm",
    },
    {
      key: "otherDocuments",
      label: "Other Documents",
      condition: () => true,
    },
  ];

  const checklist = {
    "Entity Documents": [
      {
        key: "provisionalBalanceSheet",
        label: `Provisional Balance Sheet FY ${currentFinancialYear}`,
      },
      {
        key: "auditedBalanceFYPrev1",
        label: prev1FYLabel,
      },
      {
        key: "auditedBalanceFYPrev2",
        label: prev2FYLabel,
      },
      ...entityDocs.filter((doc) => doc.condition(application?.entityType)),
    ],
    // "Entity Documents": [
    //   { key: "borrowerPanCard", label: "Entity PAN" },
    //   { key: "moa", label: "MOA" },
    //   { key: "aoa", label: "AOA" },
    //   { key: "shareholding", label: "Shareholding Pattern" },
    //   { key: "incorporationDateProof", label: "Incorporation Date Proof" },
    //   // { key: "gstDocx", label: "GST Certificate" },
    //   // { key: "partnershipDeed", label: "Partnership Deed" },
    // ],
    // "Entity Documents": entityDocs.filter(doc => doc.condition(application?.entityType)),
    "Promoter KYC": [
      { key: "AadharCard", label: "Aadhar Card" },
      { key: "panCard", label: "PAN Card" },
    ],
    "Bank Details": [
      { key: "bankStatement", label: "Last 12 Month Bank Statement" },
      { key: "slLoan", label: "Sanction Letter" },
      { key: "debitProfile", label: "Debit Profile" },
    ],
    "Security & Other Docs": [
      { key: "coLetteral", label: "Collateral Document" },
      { key: "orderCopy", label: "Order Copy" },
      { key: "contractCopy", label: "Contract Copy" },
      { key: "certification", label: "Certification" },
      { key: "creditRating", label: "Credit Rating" },
      { key: "other", label: "Other Document" }, // optional if needed
    ],
  };

  const MCA_DOC_KEYS = new Set([
    "moa",
    "aoa",
    "auditedBalanceFYPrev1",
    "auditedBalanceFYPrev2",
    "shareholding",
    "otherDocuments",
  ]);

  const proposalDocuments = useMemo(() => {
    const docs = [];
    application?.proposal?.cma && docs.push({ key: "proposal_cma", label: "CMA & Financial Report", present: true });
    application?.proposal?.onePager && docs.push({ key: "proposal_onePager", label: "One Pager Summary", present: true });
    return docs;
  }, [application?.proposal?.cma, application?.proposal?.onePager]);

  const isNonEmptyObject = (value) =>
    value != null && value.constructor === Object && Object.keys(value).length > 0;

  const hasDocContent = (item) =>
    item &&
    (item.url ||
      item.documentPath ||
      item.s3Key ||
      item.documentName ||
      item.base64 ||
      item.filename);

  const hasUploadedDocument = (docValue) => {
    if (!docValue) return false;

    if (Array.isArray(docValue)) {
      return docValue.some((item) => {
        if (Array.isArray(item)) return item.length > 0;
        if (isNonEmptyObject(item)) return !!hasDocContent(item);
        return Boolean(item);
      });
    }
    if (isNonEmptyObject(docValue)) {
      return !!hasDocContent(docValue) || Object.keys(docValue).length > 0;
    }
    return Boolean(docValue);
  };

  const getDocumentHref = (docValue) => {
    const pickHref = (v) => {
      if (!v) return null;
      if (isUrl(v)) return v;
      return v?.url ?? v?.documentPath ?? v?.s3Key ?? null;
    };
    return Array.isArray(docValue) ? pickHref(docValue[0]) : pickHref(docValue);
  };

  const getDocumentStatus = (section, docValue, docKey) => {
    const uploaded = hasUploadedDocument(docValue);

    if (section === "Entity Documents") {
      const href = getDocumentHref(docValue);

      if (MCA_DOC_KEYS.has(docKey)) {
        if (href) {
          return { uploaded: false, inProgress: false, label: "Fetched", checked: true };
        }

        if (uploaded) {
          return {
            uploaded: true,
            inProgress: false,
            label: "Uploaded",
            checked: true,
          };
        }

        if (!application?.mcaDocxStatus) {
          if (application?.isPreSubmitPreview && application?.datafetchedFromportal) {
            return { uploaded: false, inProgress: true, label: "In-progress", checked: true };
          }
          return { uploaded: false, inProgress: false, label: "Pending", checked: false };
        }

        return { uploaded: false, inProgress: true, label: "In-progress", checked: true };
      }

      // Non-MCA Entity documents are purely manual uploads
      return {
        uploaded,
        inProgress: false,
        label: uploaded ? "Uploaded" : "Pending",
        checked: uploaded,
      };
    }

    return {
      uploaded,
      inProgress: false,
      label: uploaded ? "Uploaded" : "Pending",
      checked: uploaded,
    };
  };

  const {
    label: companyBriefStatusLabel,
    checked: companyBriefChecked,
  } = getDocumentStatus(
    "Entity Documents",
    loanDoc?.companyBrief,
    "companyBrief"
  );

  const companyBriefHref = getDocumentHref(loanDoc?.companyBrief);

  return (
    <div
      ref={contentRef}
      style={{
        width: "794px",
        padding: "20px",
        backgroundColor: "#fff",
        fontFamily: "Arial, sans-serif",
        color: "#000",
      }}
    >
      <table style={tableStyle}>
        <tbody>
          {/* Basic Details */}
          <tr>
            <td colSpan={4} style={sectionHeadingStyle}>
              Basic Details
            </td>
          </tr>
          {/* <tr>
            <td style={thTdStyle}>Application No</td>
            <td style={thTdStyle}>{application.applicationNumber || "N/A"}</td>
            <td style={thTdStyle}>Status</td>
            <td style={thTdStyle}>{application.status || "N/A"}</td>
          </tr> */}
          <tr>
            <td style={thTdStyle}>Borrower Name</td>
            <td style={thTdStyle}>{application?.userInfo?.Name || application?.userInfo?.name || "N/A"}</td>
            <td style={thTdStyle}>Email</td>
            <td style={thTdStyle}>{application?.userInfo?.Email || application?.email || "N/A"}</td>
          </tr>
          <tr>
            <td style={thTdStyle}>Mobile</td>
            <td style={thTdStyle}>
              {application?.userInfo?.MobileNumber || application?.mobileNo || "N/A"}
            </td>
            <td style={thTdStyle}>GSTIN</td>
            <td style={thTdStyle}>{application.gstin || "N/A"}</td>
          </tr>
          <tr>
            <td style={thTdStyle}>Zipcode</td>
            <td style={thTdStyle}>{application.zipCode || "N/A"}</td>
            <td style={thTdStyle}>City</td>
            <td style={thTdStyle}>{application.city || "N/A"}</td>
          </tr>
          <tr>
            <td style={thTdStyle}>State</td>
            <td colSpan={3} style={thTdStyle}>{application.state || "N/A"}</td>
          </tr>

          {/* Business Details */}
          <tr>
            <td colSpan={4} style={sectionHeadingStyle}>
              Entity Details
            </td>
          </tr>
          <tr>
            <td style={thTdStyle}>Entity Name</td>
            <td style={thTdStyle}>{application.nameAsPerGST || "N/A"}</td>
            <td style={thTdStyle}>Entity Type</td>
            <td style={thTdStyle}>{application.entityType || "N/A"}</td>
          </tr>
          <tr>
            <td style={thTdStyle}>Sector</td>
            <td style={thTdStyle}>{application.sector || "N/A"}</td>
            <td style={thTdStyle}>Industry</td>
            <td style={thTdStyle}>{application.industry || "N/A"}</td>
          </tr>
          {/* <tr>
            <td style={thTdStyle}>CIN</td>
            <td style={thTdStyle}>{application.companyCin || "N/A"}</td>
          </tr> */}
          <tr>
            <td style={thTdStyle}>Udyam Registration No</td>
            <td style={thTdStyle}>
              {application.udyamRegistrationNo ||
                application.udyamRegistration ||
                "N/A"}
            </td>
            <td style={thTdStyle}>Desired Loan Amount (in INR)</td>
            <td style={thTdStyle}>
              ₹{formatWithCommas(application.fundRequirement) || "0"}
            </td>
          </tr>
          <tr>
            <td style={thTdStyle}>Company Brief</td>
            <td style={thTdStyle}>
              {application.descriptionOfBusiness || "N/A"}
            </td>
            <td style={thTdStyle}>
              {companyBriefHref ? (
                <a
                  href={companyBriefHref || "#"}
                  onClick={(e) => {
                    e.preventDefault();
                    openPresignedS3Url(companyBriefHref);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#000",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Company Brief Document
                </a>
              ) : (
                <span>Company Brief Document</span>
              )}
            </td>
            <td style={thTdStyle}>
              <input type="checkbox" checked={companyBriefChecked} readOnly />{" "}
              {companyBriefStatusLabel}
            </td>
          </tr>
          <tr>
            <td style={thTdStyle}>End use of Funds</td>
            <td style={thTdStyle}>{application.endUseOfFund || "N/A"}</td>
            <td style={thTdStyle}>Year Till Date Sales (in INR)</td>
            <td style={thTdStyle}>₹{formatWithCommas(application.totalSalesCurrentYear) || "0"}</td>
          </tr>
          <tr>
            <td style={thTdStyle}>Last FY Sales Turnover (in INR)</td>
            <td style={thTdStyle}>
              ₹{formatWithCommas(application.lastFinancialYearTurnover) || "0"}
            </td>
            <td style={thTdStyle}>
              Projected Turnover for current FY (in INR)
            </td>
            <td style={thTdStyle}>
              ₹
              {formatWithCommas(application.anticipatedGrowthRateCurrentYear) ||
                "0"}
            </td>
          </tr>
          <tr>
            <td style={thTdStyle}>Security offered</td>
            <td style={thTdStyle}>{application?.securityOffered || "N/A"}</td>
            <td style={thTdStyle}>Description of security offered </td>
            <td style={thTdStyle}>
              {application?.descriptionOfSecurity || "N/A"}
            </td>
          </tr>
          <tr>
            <td style={thTdStyle}>Estimated Collateral (in INR)</td>
            <td colSpan={3} style={thTdStyle}>
              ₹{formatWithCommas(application?.estimatedCollateralValue) || "0"}
            </td>
          </tr>

          {/* Director / Promoter Details */}
          {/* <tr>
            <td colSpan={4} style={sectionHeadingStyle}>
              Promoter / Director Details
            </td>
          </tr>
          {directors.length > 0 ? (
            directors.map((dir, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td colSpan={4} style={{ ...thTdStyle, fontWeight: "bold" }}>
                    {dir.directorName || `Director ${i + 1}`}
                  </td>
                </tr>
                <tr>
                  <td style={thTdStyle}>Designation</td>
                  <td style={thTdStyle}>{dir.directorDesignation || "N/A"}</td>
                  <td style={thTdStyle}>Shareholding %</td>
                  <td style={thTdStyle}>{dir.shareholding || "N/A"}</td>
                </tr>
                <tr>
                  <td style={thTdStyle}>PAN</td>
                  <td style={thTdStyle}>{dir.directorPan || "N/A"}</td>
                  <td style={thTdStyle}>Aadhaar</td>
                  <td style={thTdStyle}>{dir.directorAadhaar || "N/A"}</td>
                </tr>
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={4} style={thTdStyle}>
                No Promoter/Director Details Available
              </td>
            </tr>
          )} */}

          {/* Documents */}
          {proposalDocuments.length > 0 && (
            <>
              <tr>
                <td colSpan={4} style={sectionHeadingStyle}>
                  Proposal Documents
                </td>
              </tr>
              {proposalDocuments
                .reduce((rows, doc, idx) => {
                  if (idx % 2 === 0) rows.push([doc]);
                  else rows[rows.length - 1].push(doc);
                  return rows;
                }, [])
                .map((row, rowIdx) => (
                  <tr key={`proposal-${rowIdx}`}>
                    {row.map((doc, colIdx) => {
                      const docUrl = doc.key === "proposal_cma" ? application?.proposal?.cma : application?.proposal?.onePager;
                      const handleClick = (e) => {
                        e.preventDefault();
                        openPresignedS3Url(docUrl);
                      };
                      const handleKeyDown = (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openPresignedS3Url(docUrl);
                        }
                      };
                      return (
                        <React.Fragment key={`proposal-cell-${colIdx}`}>
                          <td style={thTdStyle}>
                            <span 
                              role="button" 
                              tabIndex={0} 
                              onClick={handleClick}
                              onKeyDown={handleKeyDown}
                              style={{ color: '#000', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              {doc.label}
                            </span>
                          </td>
                          <td style={thTdStyle}>
                            <input type="checkbox" checked={!!doc.present} readOnly /> {doc.present ? "Uploaded" : "Pending"}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    {row.length < 2 && (
                      <>
                        <td style={thTdStyle}></td>
                        <td style={thTdStyle}></td>
                      </>
                    )}
                  </tr>
                ))}
            </>
          )}
          {Object.entries(checklist).map(([section, docs], i) => (
            <React.Fragment key={i}>
              {/* Section Heading */}
              <tr>
                <td colSpan={4} style={sectionHeadingStyle}>
                  {section}
                </td>
              </tr>

              {/* Docs - two per row */}
              {docs
                .reduce((rows, doc, idx) => {
                  if (idx % 2 === 0) rows.push([doc]); // start new row
                  else rows[rows.length - 1].push(doc); // add second doc to same row
                  return rows;
                }, [])
                .map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((doc, colIdx) => {
                      const { key, label: docLabel } = doc ?? {};
                      const docValue =
                        key === "other"
                          ? loanDoc?.other ?? loanDoc?.otherDocuments
                          : loanDoc?.[key];
                      const { label: statusLabel, checked } = getDocumentStatus(
                        section,
                        docValue,
                        key
                      );
                      const href = getDocumentHref(docValue);
                      const isOtherDocsArray =
                        (key === "other" || key === "otherDocuments") &&
                        Array.isArray(docValue) &&
                        docValue.length > 0;

                      const handleDocumentClick = (e) => {
                        e.preventDefault();
                        if (isOtherDocsArray) {
                          openMultipleDocumentUrls(docValue, docLabel || "Other Documents");
                        } else if (href) {
                          openPresignedS3Url(href);
                        }
                      };
                      const labelContent = href || isOtherDocsArray ? (
                        <a
                          href={href || "#"}
                          onClick={handleDocumentClick}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#000", textDecoration: "underline", cursor: "pointer" }}
                        >
                          {docLabel}
                        </a>
                      ) : (
                        <span>{docLabel}</span>
                      );

                      return (
                        <React.Fragment key={colIdx}>
                          <td style={thTdStyle}>{labelContent}</td>
                          <td style={thTdStyle}>
                            <input type="checkbox" checked={checked} readOnly /> {statusLabel}
                          </td>
                        </React.Fragment>
                      );
                    })}

                    {/* If odd count, fill empty cells */}
                    {row.length < 2 && (
                      <>
                        <td style={thTdStyle}></td>
                        <td style={thTdStyle}></td>
                      </>
                    )}
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default LoanApplicationPDF;
